import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Box, Button, Chip, IconButton, Stack, TextField, Typography } from '@mui/material';
import { FieldArray, useField } from 'formik';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';

import StatusChip from '@sophocles/components/StatusChip';

// Formik-bound editor for an array of strings (email addresses,
// phone numbers, etc.).  Renders a row per entry with a remove
// button, plus an "Add" button at the bottom that appends an empty
// string for the user to fill in.
//
// `readOnly` switches to a display-only render: each entry is a
// labeled card showing the value (and any known metadata for
// object-shaped entries — number, carrier, verified) with the
// remove button still present.  Use for legacy SMS where editing
// the number is no longer supported but removal still is.
//
// Per-row error display: yup schemas like
// `array().of(string().email())` produce per-index error strings
// at `meta.error[index]`.  Touched tracking only fires on blur.
export default function SettingsListField({
  name,
  itemLabel,
  addLabel,
  placeholder,
  emptyLabel,
  type = 'text',
  readOnly = false,
}) {
  const { t } = useTranslation();
  const [field, meta] = useField(name);
  const value = field.value || [];

  // Pull a display string out of either a bare string entry or a
  // legacy object entry (`{number, carrier, verified}` etc.).
  const getDisplayValue = entry =>
    typeof entry === 'string' ? entry : entry?.number || entry?.phone || entry?.email || entry?.value || '';

  return (
    <FieldArray name={name}>
      {({ push, remove }) => (
        <Box>
          {value.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ pb: 1 }}>
              {emptyLabel}
            </Typography>
          ) : (
            <Stack spacing={1.5}>
              {value.map((entry, idx) => {
                const itemError = _.get(meta.error, idx);
                const itemTouched = _.get(meta.touched, idx);
                const showError = Boolean(itemTouched && itemError);
                const displayValue = getDisplayValue(entry);

                if (readOnly) {
                  const isObject = typeof entry === 'object' && entry !== null;
                  const carrier = isObject ? entry.carrier || entry.sms_carrier : null;
                  const verified = isObject ? Boolean(entry.verified || entry.sms_verified) : false;
                  return (
                    <Stack
                      key={idx}
                      direction="row"
                      alignItems="center"
                      spacing={1.5}
                      sx={{
                        py: 1,
                        px: 1.5,
                        borderRadius: 1,
                        border: 1,
                        borderColor: 'divider',
                        backgroundColor: 'background.paper',
                      }}
                    >
                      <Stack spacing={0.25} sx={{ flexGrow: 1, minWidth: 0 }}>
                        {itemLabel ? (
                          <Typography variant="caption" color="text.secondary">
                            {itemLabel}
                          </Typography>
                        ) : null}
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {displayValue || t('general.unknown', 'Unknown')}
                          </Typography>
                          {verified ? (
                            <StatusChip label={t('userSettings.verified', 'Verified')} color="ok" sx={{ height: 22 }} />
                          ) : null}
                          {carrier ? (
                            <Chip size="small" label={carrier} variant="outlined" sx={{ height: 22 }} />
                          ) : null}
                        </Stack>
                      </Stack>
                      <IconButton onClick={() => remove(idx)} aria-label={t('actions.remove', 'Remove')} edge="end">
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Stack>
                  );
                }

                return (
                  <Stack key={idx} direction="row" spacing={1} alignItems="flex-start">
                    <TextField
                      name={`${name}[${idx}]`}
                      type={type}
                      value={displayValue}
                      onChange={e => {
                        const next = [...value];
                        next[idx] = e.target.value;
                        field.onChange({ target: { name, value: next } });
                      }}
                      onBlur={() => field.onBlur({ target: { name: `${name}[${idx}]`, value: entry } })}
                      label={itemLabel}
                      placeholder={placeholder}
                      error={showError}
                      helperText={showError ? itemError : ''}
                      fullWidth
                    />
                    <IconButton onClick={() => remove(idx)} aria-label={t('actions.remove', 'Remove')} sx={{ mt: 1 }}>
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Stack>
                );
              })}
            </Stack>
          )}
          {addLabel ? (
            <Box sx={{ pt: 1 }}>
              <Button size="small" startIcon={<AddIcon />} onClick={() => push('')}>
                {addLabel}
              </Button>
            </Box>
          ) : null}
        </Box>
      )}
    </FieldArray>
  );
}
